import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { Gate } from "../../../../../http/border/types/gates";
import { useBorderSystemsApi } from "../../../../../http/border/useBorderSystems";
import { useGatesApi } from "../../../../../http/border/useGates";
import { SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import { itemLabel } from "../../../../../utils";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import MultiCheckbox from "../../../../ui/MultiCheckbox";
import Numeric from "../../../../ui/Numeric";
import { CustomAsyncSelect } from "../../../../ui/select/CustomSelect";

interface GateFormBody {
  item: SearchAdminItem | null;
  systemIds: string[];
  order: number | null;
  visibility: VisibilityScopeValue;
}

interface GateFormProps {
  gate?: Gate;
  onClose: () => void;
  isDisabled?: boolean;
  isLoading?: boolean;
}

const GateForm: FC<GateFormProps> = ({ gate, onClose, isDisabled, isLoading }: GateFormProps) => {
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);

  const gatesApi = useGatesApi();
  const borderSystemsApi = useBorderSystemsApi();
  const itemsApi = useItemsApi();

  const borderSystems = borderSystemsApi.useAdminBorderSystems({ limit: 100 }, aborter.current.signal);

  const defaultValues: GateFormBody = {
    item: gate?.item ?? null,
    systemIds: gate?.systems.map((x) => x.id) ?? [],
    order: gate?.order ?? 50,
    visibility: gate?.visibility ?? "hidden",
  };

  const onSubmit = () => async (formData: GateFormBody) => {
    setIsSubmitting(true);

    if (gate) {
      try {
        await gatesApi.updateAdminGate(gate, {
          systemIds: formData.systemIds,
          order: formData.order!,
          visibility: formData.visibility,
        });
        onClose();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      try {
        await gatesApi.insertAdminGate({
          itemId: formData.item?.id!,
          systemIds: formData.systemIds,
          order: formData.order!,
          visibility: formData.visibility,
        });
        onClose();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Form<GateFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isDisabled || isLoading || isSubmitting}
      secondaryButton={
        <Button buttonProps={{ onClick: () => onClose() }} type="secondary">
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={onSubmit}
    >
      {({ control, setValue, reset, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [gate]);

        const systemIds = watch("systemIds");

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                      {({ labelId }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef(new AbortController());

                        const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                        // Fetch initial data on mount
                        useEffect(() => {
                          loadOptions(searchTerm.current).then(setDefaultOptions);
                        }, []);

                        return (
                          <CustomAsyncSelect
                            {...field}
                            inputId={labelId}
                            defaultOptions={defaultOptions}
                            isDisabled={isDisabled || isLoading || isSubmitting}
                            loadOptions={loadOptions}
                            isClearable
                            getOptionLabel={itemLabel}
                            getOptionValue={(x) => x.id}
                            isInvalid={fieldState.invalid}
                            onChange={(value) => setValue("item", value)}
                          />
                        );
                      }}
                    </FormField>
                    <FormField label={i18n.translation.systems.plural}>
                      <MultiCheckbox
                        disabled={isDisabled}
                        values={
                          borderSystems.data?.items.map((x) => ({
                            id: x.id,
                            title: x.name,
                            selected: systemIds.includes(x.id),
                          })) ?? []
                        }
                        onUpdate={(id, checked) => {
                          setValue("systemIds", checked ? [...systemIds, id] : systemIds.filter((x) => x !== id));
                        }}
                      />
                    </FormField>
                  </>
                );
              }}
            />
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isDisabled || isLoading || isSubmitting}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default GateForm;
